import { useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { useLogin } from "~/api/queries";
import { type AuthProviderOption, fetchAuthProviders, safeReturnTo } from "~/auth/oidc";
import { Button } from "~/foundation/components/Button";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { SITE_CONFIG } from "~/site";

export function LoginScene() {
  const { t } = useTranslation();
  const api = useApi();
  const [email, setEmail] = useState("");
  const login = useLogin();
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: "/login" });
  useDocumentTitle(t("Sign in"));

  // The adapter decides how sign-in works: the platform redirects to its identity provider,
  // the fixture accepts any email. The scene renders whichever the adapter offers.
  const signIn = api.auth.signIn;

  // GitHub (2026-09-24) is offered only when the auth server lists it, i.e. once it is
  // configured there — a button that could only fail is never shown.
  const [providers, setProviders] = useState<AuthProviderOption[]>([]);
  useEffect(() => {
    if (!signIn) {
      return;
    }
    let live = true;
    void fetchAuthProviders().then((list) => {
      if (live) setProviders(list);
    });
    return () => {
      live = false;
    };
  }, [signIn]);
  const github = providers.some((p) => p.id === "github");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim()) {
      return;
    }
    login.mutate(email.trim(), {
      onSuccess: () => {
        void navigate({ to: "/" });
      },
    });
  };

  return (
    <div className="flex h-dvh items-center justify-center bg-bg px-4">
      <div className="flex w-[360px] max-w-full flex-col gap-4 rounded-md border border-divider bg-menu-bg p-6 shadow-menu">
        <div className="flex flex-col gap-1">
          <span className="text-h2 font-semibold text-text">{SITE_CONFIG.appName}</span>
          <Text variant="secondary">{t("Sign in to continue")}</Text>
        </div>

        {signIn ? (
          <>
            <Button variant="primary" fullWidth onClick={() => void signIn(safeReturnTo(redirect))}>
              {t("Continue with Docit")}
            </Button>
            {github ? (
              <Button
                variant="neutral"
                fullWidth
                onClick={() => void signIn(safeReturnTo(redirect), "github")}
              >
                {t("Continue with GitHub")}
              </Button>
            ) : null}
            <Text variant="small" className="text-center">
              {t("You will be returned here once you have signed in.")}
            </Text>
          </>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Input
              label={t("Email")}
              type="email"
              value={email}
              autoFocus
              fullWidth
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
            />
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={login.isPending || email.trim() === ""}
            >
              {t("Continue")}
            </Button>
            <Text variant="small" className="text-center">
              {t("Any email works while the app runs on fixture data.")}
            </Text>
          </form>
        )}
      </div>
    </div>
  );
}
